import React from 'react';
import { Modal, Button, Alert, Badge, Typography } from 'antd';
import { ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface UnsavedChangesModalProps {
  open: boolean;
  saving: boolean;
  /** Closes the modal and keeps the user on the current page. Wired to the
   *  top-right `×` icon. */
  onCancel: () => void;
  /** Proceeds with the originally-requested action (e.g. navigation away)
   *  WITHOUT saving — the user's in-progress changes will be lost. */
  onDiscardAndContinue: () => void;
  /** Saves the user's changes, then proceeds with the originally-requested
   *  action. */
  onSave: () => void;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  open,
  saving,
  onCancel,
  onDiscardAndContinue,
  onSave,
}) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      centered
      // Top-right `×` icon is the cancel affordance — clicking it (or
      // pressing Esc) closes the modal and leaves the user on the current
      // page with their unsaved changes intact. Mask-click is still
      // disabled so the user can't accidentally dismiss the prompt.
      closable={!saving}
      maskClosable={false}
      width={480}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
          <Badge
            count="Unsaved changes"
            style={{
              backgroundColor: '#fff1f0',
              color: '#cf1322',
              border: '1px solid #ffa39e',
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 4,
            }}
          />
        </div>
      }
      styles={{
        header: {
          backgroundColor: '#fff1f0',
          marginLeft: '-24px',
          marginRight: '-24px',
          marginTop: '-20px',
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingTop: '16px',
          paddingBottom: '16px',
          borderBottom: '1px solid #ffa39e',
        },
      }}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            borderTop: '1px solid #f0f0f0',
            paddingTop: 16,
            marginLeft: '-24px',
            marginRight: '-24px',
            paddingLeft: '24px',
            paddingRight: '24px',
            marginBottom: '-20px',
            paddingBottom: '20px',
          }}
        >
          <Button
            danger
            onClick={onDiscardAndContinue}
            disabled={saving}
          >
            Continue without Saving
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={onSave}
          >
            Save and Continue
          </Button>
        </div>
      }
    >
      <div style={{ paddingTop: 8, paddingBottom: 8 }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
          You have unsaved changes
        </Title>
        <Paragraph style={{ marginBottom: 16, color: '#595959' }}>
          You are about to leave this page. Please save your changes to preserve your work progress.
        </Paragraph>
        <Alert
          message='Your progress will be lost if you choose "Continue without Saving".'
          type="error"
          showIcon
        />
      </div>
    </Modal>
  );
};

export default UnsavedChangesModal;
